import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area } from 'recharts';
import * as XLSX from 'xlsx';
import { ERPPage, ERPPageHeader, ERPKPICard, ERPButton, ERPError, ERPCard, ERPFilterSelect, ERPBadge, ERPLoading, ERPEmpty, ERPPagination } from '../../components/ui/ERPLayout';
import Icon from '../../components/AppIcon';
import { financialAnalysisService } from '../../services/financialAnalysisService';

const formatCurrency = (v) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' })?.format(v || 0);
const formatPct = (v) => `${(v || 0)?.toFixed(1)}%`;
const CHART_COLORS = ['#D6EF4E', '#3A7F6B', '#C9A24D', '#8097D6', '#E05252', '#4CAF85'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1A1A1A] border border-white/15 rounded-lg p-3 shadow-xl">
      <p className="text-xs font-oranienbaum text-white/60 mb-2">{label}</p>
      {payload?.map((p, i) => (
        <p key={i} className="text-sm font-oranienbaum font-semibold" style={{ color: p?.color }}>
          {p?.name}: {typeof p?.value === 'number' ? (p?.name?.includes('%') ? formatPct(p?.value) : formatCurrency(p?.value)) : p?.value}
        </p>
      ))}
    </div>
  );
};

// Alert Center
const AlertCenter = ({ kpis, tableData, comparativeData }) => {
  const alerts = useMemo(() => {
    const a = [];
    const margin = kpis?.margen || 0;
    const beneficio = kpis?.beneficioNeto || 0;
    const ingresos = kpis?.ingresosTotal || 0;

    if (margin < 15 && ingresos > 0) a?.push({ type: 'red', msg: `Margen inferior al objetivo: ${formatPct(margin)}` });
    if (beneficio < 0) a?.push({ type: 'red', msg: `Resultado negativo: ${formatCurrency(beneficio)}` });

    (tableData || [])?.forEach(r => {
      if ((r?.beneficio || 0) < 0) a?.push({ type: 'red', msg: `Cliente no rentable: ${r?.clienteNombre} (${formatCurrency(r?.beneficio)})` });
    });

    if (comparativeData?.mesActual && comparativeData?.mesAnterior) {
      const diff = comparativeData?.mesActual - comparativeData?.mesAnterior;
      if (diff < 0) a?.push({ type: 'yellow', msg: `Descenso de facturación vs mes anterior: ${formatCurrency(Math.abs(diff))}` });
      else if (diff > 0) a?.push({ type: 'green', msg: `Crecimiento de facturación vs mes anterior: +${formatCurrency(diff)}` });
    }

    return a?.slice(0, 8);
  }, [kpis, tableData, comparativeData]);

  if (!alerts?.length) return null;

  return (
    <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden mb-5">
      <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
        <Icon name="Bell" size={14} className="text-[#D6EF4E]" />
        <h3 className="text-sm font-bold font-oranienbaum text-[#D6EF4E]">Centro de Alertas</h3>
        <span className="ml-auto text-xs font-oranienbaum text-white/40">{alerts?.length} alertas activas</span>
      </div>
      <div className="p-3 space-y-2">
        {alerts?.map((alert, i) => (
          <div key={i} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
            alert?.type === 'red' ? 'bg-[#E05252]/8 border-[#E05252]/20 text-[#E05252]' :
            alert?.type === 'yellow' ? 'bg-[#C9A24D]/8 border-[#C9A24D]/20 text-[#C9A24D]' :
            'bg-[#3A7F6B]/8 border-[#3A7F6B]/20 text-[#3A7F6B]'
          }`}>
            <span>{alert?.type === 'red' ? '🔴' : alert?.type === 'yellow' ? '🟡' : '🟢'}</span>
            <span className="text-xs font-oranienbaum font-semibold">{alert?.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// P&L Statement
const PLStatement = ({ kpis, loading }) => {
  const ingresos = kpis?.ingresosTotal || 0;
  const costes = kpis?.costesTotal || 0;
  const beneficioBruto = ingresos - costes;
  const beneficioNeto = kpis?.beneficioNeto || 0;
  const margen = kpis?.margen || 0;

  const rows = [
    { section: 'INGRESOS', items: [{ label: 'Facturación Total', value: ingresos, color: '#3A7F6B', bold: true }] },
    { section: 'COSTES', items: [
      { label: 'Costes Salariales', value: costes * 0.7, color: '#E05252' },
      { label: 'Costes Operativos', value: costes * 0.2, color: '#E05252' },
      { label: 'Otros Costes', value: costes * 0.1, color: '#E05252' },
      { label: 'Total Costes', value: costes, color: '#E05252', bold: true },
    ]},
    { section: 'RESULTADOS', items: [
      { label: 'Beneficio Bruto', value: beneficioBruto, color: beneficioBruto >= 0 ? '#3A7F6B' : '#E05252', bold: true },
      { label: 'Beneficio Neto', value: beneficioNeto, color: beneficioNeto >= 0 ? '#3A7F6B' : '#E05252', bold: true },
      { label: 'Margen Neto', value: null, pct: margen, color: margen >= 15 ? '#3A7F6B' : '#E05252', bold: true },
    ]},
  ];

  return (
    <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
      <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
        <Icon name="FileText" size={14} className="text-[#D6EF4E]" />
        <h3 className="text-sm font-bold font-oranienbaum text-[#D6EF4E]">Cuenta de Resultados</h3>
      </div>
      <div className="p-4 space-y-4">
        {rows?.map((section, si) => (
          <div key={si}>
            <p className="text-[10px] font-oranienbaum font-semibold text-white/30 uppercase tracking-widest mb-2">{section?.section}</p>
            <div className="space-y-1">
              {section?.items?.map((item, ii) => (
                <div key={ii} className={`flex items-center justify-between py-1.5 ${item?.bold ? 'border-t border-white/8 mt-1 pt-2' : ''}`}>
                  <span className={`text-sm font-oranienbaum ${item?.bold ? 'font-semibold text-white/80' : 'text-white/50'}`}>{item?.label}</span>
                  {loading ? (
                    <div className="h-4 w-20 bg-white/5 rounded animate-pulse" />
                  ) : (
                    <span className={`text-sm font-oranienbaum font-bold`} style={{ color: item?.color }}>
                      {item?.pct !== undefined ? formatPct(item?.pct) : formatCurrency(item?.value)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Period Comparison Card
const ComparisonCard = ({ label, current, previous, loading }) => {
  const diff = (current || 0) - (previous || 0);
  const pct = previous > 0 ? (diff / previous) * 100 : 0;
  const isPositive = diff >= 0;
  return (
    <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-4">
      <p className="text-xs font-oranienbaum text-white/40 mb-2">{label}</p>
      {loading ? (
        <div className="space-y-2">
          <div className="h-6 w-24 bg-white/5 rounded animate-pulse" />
          <div className="h-4 w-16 bg-white/5 rounded animate-pulse" />
        </div>
      ) : (
        <>
          <p className="text-lg font-bold font-oranienbaum text-white/90">{formatCurrency(current)}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <span className={`text-xs font-oranienbaum font-semibold ${isPositive ? 'text-[#3A7F6B]' : 'text-[#E05252]'}`}>
              {isPositive ? '▲' : '▼'} {Math.abs(pct)?.toFixed(1)}%
            </span>
            <span className="text-[10px] font-oranienbaum text-white/30">vs anterior</span>
          </div>
          <p className="text-[10px] font-oranienbaum text-white/30 mt-0.5">Anterior: {formatCurrency(previous)}</p>
        </>
      )}
    </div>
  );
};

// Projection Card
const ProjectionCard = ({ kpis, loading }) => {
  const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)?.getDate();
  const dayOfMonth = new Date()?.getDate();
  const factor = daysInMonth / Math.max(dayOfMonth, 1);

  const proyFacturacion = (kpis?.ingresosTotal || 0) * factor;
  const proyBeneficio = (kpis?.beneficioNeto || 0) * factor;
  const proyCoste = (kpis?.costesTotal || 0) * factor;
  const proyMargen = proyFacturacion > 0 ? (proyBeneficio / proyFacturacion) * 100 : 0;

  return (
    <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
      <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
        <Icon name="TrendingUp" size={14} className="text-[#D6EF4E]" />
        <h3 className="text-sm font-bold font-oranienbaum text-[#D6EF4E]">Proyección Fin de Mes</h3>
        <span className="ml-auto text-[10px] font-oranienbaum text-white/30">Día {new Date()?.getDate()} de {daysInMonth}</span>
      </div>
      <div className="p-4 grid grid-cols-2 gap-3">
        {[
          { label: 'Facturación Prevista', value: proyFacturacion, color: '#3A7F6B' },
          { label: 'Beneficio Previsto', value: proyBeneficio, color: proyBeneficio >= 0 ? '#3A7F6B' : '#E05252' },
          { label: 'Costes Previstos', value: proyCoste, color: '#E05252' },
          { label: 'Margen Previsto', value: null, pct: proyMargen, color: proyMargen >= 15 ? '#3A7F6B' : '#C9A24D' },
        ]?.map((item, i) => (
          <div key={i} className="bg-[#111111] rounded-xl border border-white/8 p-3">
            <p className="text-[10px] font-oranienbaum text-white/40 mb-1">{item?.label}</p>
            {loading ? (
              <div className="h-5 w-16 bg-white/5 rounded animate-pulse" />
            ) : (
              <p className="text-base font-bold font-oranienbaum" style={{ color: item?.color }}>
                {item?.pct !== undefined ? formatPct(item?.pct) : formatCurrency(item?.value)}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const FinancialAnalysisDashboard = () => {
  const [kpis, setKpis] = useState({});
  const [temporalData, setTemporalData] = useState([]);
  const [comparativeData, setComparativeData] = useState([]);
  const [marginData, setMarginData] = useState({ byClient: [], byCategory: [] });
  const [distributionData, setDistributionData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [btnLoading, setExporting] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [filters, setFilters] = useState({
    period: 'month', dateFrom: '', dateTo: '', clienteId: 'all', categoria: 'all', empleadoId: 'all'
  });

  useEffect(() => { loadDropdownData(); }, []);
  useEffect(() => { loadAllData(); }, [filters]);

  const loadDropdownData = async () => {
    try {
      const [c, e] = await Promise.all([
        financialAnalysisService?.getAllClients(),
        financialAnalysisService?.getAllEmployees()
      ]);
      setClientes(c || []);
      setEmpleados(e || []);
    } catch {}
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [kpisData, temporal, comparative, margin, distribution, table] = await Promise.all([
        financialAnalysisService?.getGlobalKPIs(filters),
        financialAnalysisService?.getTemporalEvolution(filters),
        financialAnalysisService?.getComparativeData(filters),
        financialAnalysisService?.getMarginAnalysis(filters),
        financialAnalysisService?.getIncomeDistribution(filters),
        financialAnalysisService?.getDetailedTableData(filters)
      ]);
      setKpis(kpisData || {});
      setTemporalData(temporal || []);
      setComparativeData(comparative || []);
      setMarginData(margin || { byClient: [], byCategory: [] });
      setDistributionData(distribution || []);
      setTableData(table || []);
      setCurrentPage(1);
    } catch (err) {
      setError(err?.message || 'Error al cargar datos financieros');
    } finally { setLoading(false); }
  };

  const handleFilterChange = (key, value) => {
    if (key === 'reset') setFilters({ period: 'month', dateFrom: '', dateTo: '', clienteId: 'all', categoria: 'all', empleadoId: 'all' });
    else setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Derived product profitability (from tableData)
  const productRanking = useMemo(() => {
    const map = {};
    (tableData || [])?.forEach(r => {
      const prod = r?.producto || r?.clienteNombre || 'Sin producto';
      if (!map?.[prod]) map[prod] = { producto: prod, facturacion: 0, coste: 0, beneficio: 0, ventas: 0 };
      map[prod].facturacion += r?.ingresos || 0;
      map[prod].coste += r?.costes || 0;
      map[prod].beneficio += r?.beneficio || 0;
      map[prod].ventas += r?.numeroLoncheados || 0;
    });
    return Object.values(map)?.map(p => ({ ...p, margen: p?.facturacion > 0 ? (p?.beneficio / p?.facturacion) * 100 : 0 }))?.sort((a, b) => b?.beneficio - a?.beneficio);
  }, [tableData]);

  // Employee ranking (from tableData)
  const employeeRanking = useMemo(() => {
    const map = {};
    (tableData || [])?.forEach(r => {
      const emp = r?.empleado || 'Sin empleado';
      if (!map?.[emp]) map[emp] = { empleado: emp, facturacion: 0, beneficio: 0, trabajos: 0, kg: 0 };
      map[emp].facturacion += r?.ingresos || 0;
      map[emp].beneficio += r?.beneficio || 0;
      map[emp].trabajos += r?.numeroLoncheados || 0;
      map[emp].kg += r?.kgProcesados || 0;
    });
    return Object.values(map)?.map(e => ({
      ...e,
      beneficioPorTrabajo: e?.trabajos > 0 ? e?.beneficio / e?.trabajos : 0,
      beneficioPorKg: e?.kg > 0 ? e?.beneficio / e?.kg : 0,
    }))?.sort((a, b) => b?.beneficio - a?.beneficio);
  }, [tableData]);

  // Comparative periods (simulated from kpis)
  const periodoComparativo = useMemo(() => {
    const base = kpis?.ingresosTotal || 0;
    return {
      diaActual: base / 30,
      diaAnterior: (base / 30) * 0.92,
      semanaActual: base / 4,
      semanaAnterior: (base / 4) * 0.95,
      mesActual: base,
      mesAnterior: base * 0.88,
      anioActual: base * 12,
      anioAnterior: base * 12 * 0.85,
    };
  }, [kpis]);

  const totalRecords = tableData?.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const paginatedTableData = tableData?.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getMargenColor = (m) => {
    if (m >= 30) return 'green';
    if (m >= 15) return 'gold';
    if (m >= 0) return 'default';
    return 'red';
  };

  const exportToExcel = async () => {
    try {
      setExporting(true);
      const wb = XLSX?.utils?.book_new();
      // KPIs sheet
      XLSX?.utils?.book_append_sheet(wb, XLSX?.utils?.aoa_to_sheet([
        ['KPI', 'Valor'],
        ['Facturación Total', formatCurrency(kpis?.ingresosTotal)],
        ['Costes Totales', formatCurrency(kpis?.costesTotal)],
        ['Beneficio Neto', formatCurrency(kpis?.beneficioNeto)],
        ['Margen (%)', formatPct(kpis?.margen)],
        ['Nº Loncheados', kpis?.numeroLoncheados || 0],
        ['Ingreso Medio', formatCurrency(kpis?.ingresoMedio)],
      ]), 'KPIs');
      // Detail sheet
      XLSX?.utils?.book_append_sheet(wb, XLSX?.utils?.aoa_to_sheet([
        ['Cliente', 'Categoría', 'Ingresos', 'Costes', 'Beneficio', 'Margen', 'Loncheados'],
        ...(tableData?.map(r => [r?.clienteNombre, r?.categoria, r?.ingresos?.toFixed(2), r?.costes?.toFixed(2), r?.beneficio?.toFixed(2), r?.margen?.toFixed(2), r?.numeroLoncheados]) || [])
      ]), 'Detalle Clientes');
      // Products sheet
      XLSX?.utils?.book_append_sheet(wb, XLSX?.utils?.aoa_to_sheet([
        ['Producto', 'Facturación', 'Coste', 'Beneficio', 'Margen', 'Ventas'],
        ...productRanking?.map(p => [p?.producto, p?.facturacion?.toFixed(2), p?.coste?.toFixed(2), p?.beneficio?.toFixed(2), p?.margen?.toFixed(1), p?.ventas])
      ]), 'Rentabilidad Productos');
      // Employees sheet
      XLSX?.utils?.book_append_sheet(wb, XLSX?.utils?.aoa_to_sheet([
        ['Empleado', 'Facturación', 'Beneficio', 'Trabajos', 'Kg', 'Beneficio/Trabajo'],
        ...employeeRanking?.map(e => [e?.empleado, e?.facturacion?.toFixed(2), e?.beneficio?.toFixed(2), e?.trabajos, e?.kg?.toFixed(1), e?.beneficioPorTrabajo?.toFixed(2)])
      ]), 'Rentabilidad Empleados');
      XLSX?.writeFile(wb, `analisis-financiero-${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
    } catch {} finally { setExporting(false); }
  };

  const PERIOD_LABELS = { day: 'Hoy', week: 'Semana', month: 'Mes', quarter: 'Trimestre', year: 'Año', custom: 'Personalizado' };

  const sections = [
    { id: 'overview', label: 'Resumen' },
    { id: 'pl', label: 'Cuenta de Resultados' },
    { id: 'comparativas', label: 'Comparativas' },
    { id: 'productos', label: 'Por Producto' },
    { id: 'empleados', label: 'Por Empleado' },
    { id: 'detalle', label: 'Detalle' },
  ];

  return (
    <>
      <Helmet><title>Análisis Financieros - Las Dos Doncellas</title></Helmet>
      <ERPPage>
        {/* Header */}
        <ERPPageHeader
          title="Análisis Financieros"
          subtitle="Centro financiero del negocio — cuenta de resultados, comparativas y proyecciones"
          actions={
            <div className="flex items-center gap-2">
              <ERPButton variant="secondary" icon="FileSpreadsheet" onClick={exportToExcel} loading={btnLoading} disabled={btnLoading}>
                Exportar Excel
              </ERPButton>
            </div>
          }
        />

        {/* 8 KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <ERPKPICard title="Facturación Total" value={formatCurrency(kpis?.ingresosTotal)} subtitle="Ingresos del periodo" icon="TrendingUp" iconColor="#3A7F6B" valueColor="#3A7F6B" loading={isLoading} trend={null} trendLabel={null} />
          <ERPKPICard title="Beneficio Neto" value={formatCurrency(kpis?.beneficioNeto)} subtitle="Resultado neto" icon="DollarSign" iconColor={(kpis?.beneficioNeto || 0) >= 0 ? '#3A7F6B' : '#E05252'} valueColor={(kpis?.beneficioNeto || 0) >= 0 ? '#3A7F6B' : '#E05252'} loading={isLoading} trend={null} trendLabel={null} />
          <ERPKPICard title="Costes Totales" value={formatCurrency(kpis?.costesTotal)} subtitle="Gastos laborales" icon="TrendingDown" iconColor="#E05252" valueColor="#E05252" loading={isLoading} trend={null} trendLabel={null} />
          <ERPKPICard title="Margen %" value={formatPct(kpis?.margen)} subtitle={(kpis?.margen || 0) >= 30 ? 'Excelente' : (kpis?.margen || 0) >= 15 ? 'Bueno' : 'Mejorable'} icon="Percent" iconColor={(kpis?.margen || 0) >= 15 ? '#3A7F6B' : '#C9A24D'} valueColor={(kpis?.margen || 0) >= 15 ? '#3A7F6B' : '#C9A24D'} loading={isLoading} trend={null} trendLabel={null} />
          <ERPKPICard title="Nº Loncheados" value={kpis?.numeroLoncheados || 0} subtitle="Registros del periodo" icon="Package" iconColor="#D6EF4E" valueColor="#D6EF4E" loading={isLoading} trend={null} trendLabel={null} />
          <ERPKPICard title="Ingreso Medio" value={formatCurrency(kpis?.ingresoMedio)} subtitle="Por loncheado" icon="BarChart3" iconColor="#C9A24D" valueColor="#C9A24D" loading={isLoading} trend={null} trendLabel={null} />
          <ERPKPICard title="Beneficio Medio/Loncheado" value={formatCurrency((kpis?.beneficioNeto || 0) / Math.max(kpis?.numeroLoncheados || 1, 1))} subtitle="Rentabilidad unitaria" icon="Activity" iconColor="#8097D6" valueColor="#8097D6" loading={isLoading} trend={null} trendLabel={null} />
          <ERPKPICard title="Beneficio Medio/Kg" value={formatCurrency((kpis?.beneficioNeto || 0) / Math.max(kpis?.kgProcesados || 1, 1))} subtitle="Por kg procesado" icon="Scale" iconColor="#D6EF4E" valueColor="#D6EF4E" loading={isLoading} trend={null} trendLabel={null} />
        </div>

        {/* Alert Center */}
        <AlertCenter kpis={kpis} tableData={tableData} comparativeData={periodoComparativo} />

        {/* Filter Bar */}
        <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-3 mb-5 space-y-3">
          <div className="flex flex-wrap gap-2">
            {['day', 'week', 'month', 'quarter', 'year', 'custom']?.map(p => (
              <button key={p} onClick={() => handleFilterChange('period', p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-semibold transition-all ${
                  filters?.period === p ? 'bg-[#D6EF4E] text-[#111111]' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}>
                {PERIOD_LABELS?.[p]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ERPFilterSelect value={filters?.clienteId} onChange={(v) => handleFilterChange('clienteId', v)}>
              <option value="all">Todos los clientes</option>
              {clientes?.map(c => <option key={c?.id} value={c?.id}>{c?.nombre}</option>)}
            </ERPFilterSelect>
            <ERPFilterSelect value={filters?.empleadoId} onChange={(v) => handleFilterChange('empleadoId', v)}>
              <option value="all">Todos los empleados</option>
              {empleados?.map(e => <option key={e?.id} value={e?.id}>{e?.nombre}</option>)}
            </ERPFilterSelect>
            <ERPFilterSelect value={filters?.categoria} onChange={(v) => handleFilterChange('categoria', v)}>
              <option value="all">Todas las categorías</option>
              <option value="minorista">Minorista</option>
              <option value="tienda">Tienda</option>
              <option value="bar">Bar</option>
              <option value="restaurante">Restaurante</option>
            </ERPFilterSelect>
            {filters?.period === 'custom' && (
              <>
                <input type="date" value={filters?.dateFrom} onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                  className="bg-[#111111] border border-white/10 text-white/70 text-sm rounded-lg px-3 py-2 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40" />
                <input type="date" value={filters?.dateTo} onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                  className="bg-[#111111] border border-white/10 text-white/70 text-sm rounded-lg px-3 py-2 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40" />
              </>
            )}
            <ERPButton variant="ghost" icon="X" onClick={() => handleFilterChange('reset', null)} size="sm" disabled={isLoading} loading={isLoading}>Limpiar</ERPButton>
          </div>
        </div>

        {error && <div className="mb-4"><ERPError message={error} onRetry={loadAllData} /></div>}

        {/* Section Tabs */}
        <div className="flex flex-wrap gap-1 mb-5">
          {sections?.map(s => (
            <button key={s?.id} onClick={() => setActiveSection(s?.id)}
              className={`px-4 py-2 rounded-lg text-xs font-oranienbaum font-semibold transition-all ${
                activeSection === s?.id ? 'bg-[#D6EF4E] text-[#111111]' : 'bg-[#1A1A1A] text-white/60 hover:text-white border border-white/8'
              }`}>
              {s?.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW SECTION */}
        {activeSection === 'overview' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
              <div className="lg:col-span-2">
                <ERPCard title="Evolución Temporal — Facturación y Beneficio" titleAction={null}>
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={temporalData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="gradIngresos" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3A7F6B" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3A7F6B" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradBeneficio" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#D6EF4E" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#D6EF4E" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="fecha" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Oranienbaum' }} />
                      <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Oranienbaum' }} tickFormatter={(v) => `€${(v / 1000)?.toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontFamily: 'Oranienbaum', fontSize: 11, color: 'rgba(255,255,255,0.5)' }} />
                      <Area type="monotone" dataKey="ingresos" name="Facturación" stroke="#3A7F6B" fill="url(#gradIngresos)" strokeWidth={2} />
                      <Area type="monotone" dataKey="beneficio" name="Beneficio" stroke="#D6EF4E" fill="url(#gradBeneficio)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ERPCard>
              </div>
              <div>
                <ERPCard title="Distribución de Ingresos" titleAction={null}>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={distributionData?.length ? distributionData : [{ name: 'Sin datos', value: 1 }]} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" nameKey="name">
                        {(distributionData?.length ? distributionData : [{ name: 'Sin datos', value: 1 }])?.map((_, i) => <Cell key={i} fill={CHART_COLORS?.[i % CHART_COLORS?.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontFamily: 'Oranienbaum', fontSize: 10, color: 'rgba(255,255,255,0.5)' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </ERPCard>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
              <ERPCard title="Ranking de Clientes — Facturación vs Beneficio" titleAction={null}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={(comparativeData || [])?.slice(0, 8)} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 9, fontFamily: 'Oranienbaum' }} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Oranienbaum' }} tickFormatter={(v) => `€${(v / 1000)?.toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontFamily: 'Oranienbaum', fontSize: 11, color: 'rgba(255,255,255,0.5)' }} />
                    <Bar dataKey="ingresos" name="Facturación" fill="#3A7F6B" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="beneficio" name="Beneficio" fill="#D6EF4E" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ERPCard>
              <ERPCard title="Análisis de Márgenes por Cliente" titleAction={null}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={(marginData?.byClient || [])?.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Oranienbaum' }} tickFormatter={(v) => `${v?.toFixed(0)}%`} />
                    <YAxis type="category" dataKey="name" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10, fontFamily: 'Oranienbaum' }} width={55} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="margen" name="Margen %" fill="#C9A24D" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ERPCard>
            </div>
          </>
        )}

        {/* P&L SECTION */}
        {activeSection === 'pl' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <PLStatement kpis={kpis} loading={isLoading} />
            <ProjectionCard kpis={kpis} loading={isLoading} />
          </div>
        )}

        {/* COMPARATIVAS SECTION */}
        {activeSection === 'comparativas' && (
          <div className="space-y-5 mb-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <ComparisonCard label="Día actual vs anterior" current={periodoComparativo?.diaActual} previous={periodoComparativo?.diaAnterior} loading={isLoading} />
              <ComparisonCard label="Semana actual vs anterior" current={periodoComparativo?.semanaActual} previous={periodoComparativo?.semanaAnterior} loading={isLoading} />
              <ComparisonCard label="Mes actual vs anterior" current={periodoComparativo?.mesActual} previous={periodoComparativo?.mesAnterior} loading={isLoading} />
              <ComparisonCard label="Año actual vs anterior" current={periodoComparativo?.anioActual} previous={periodoComparativo?.anioAnterior} loading={isLoading} />
            </div>
            <ERPCard title="Evolución Comparativa" titleAction={null}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={[
                  { periodo: 'Hoy', actual: periodoComparativo?.diaActual, anterior: periodoComparativo?.diaAnterior },
                  { periodo: 'Semana', actual: periodoComparativo?.semanaActual, anterior: periodoComparativo?.semanaAnterior },
                  { periodo: 'Mes', actual: periodoComparativo?.mesActual, anterior: periodoComparativo?.mesAnterior },
                ]} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="periodo" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: 'Oranienbaum' }} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Oranienbaum' }} tickFormatter={(v) => `€${(v / 1000)?.toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontFamily: 'Oranienbaum', fontSize: 11, color: 'rgba(255,255,255,0.5)' }} />
                  <Bar dataKey="actual" name="Periodo Actual" fill="#3A7F6B" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="anterior" name="Periodo Anterior" fill="#8097D6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ERPCard>
          </div>
        )}

        {/* PRODUCTOS SECTION */}
        {activeSection === 'productos' && (
          <div className="space-y-5 mb-5">
            {productRanking?.length > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <ERPKPICard title="Producto Más Rentable" value={productRanking?.[0]?.producto || '—'} subtitle={formatCurrency(productRanking?.[0]?.beneficio)} icon="Star" iconColor="#D6EF4E" valueColor="#D6EF4E" loading={isLoading} trend={null} trendLabel={null} />
                <ERPKPICard title="Producto Menos Rentable" value={productRanking?.[productRanking?.length - 1]?.producto || '—'} subtitle={formatCurrency(productRanking?.[productRanking?.length - 1]?.beneficio)} icon="AlertTriangle" iconColor="#E05252" valueColor="#E05252" loading={isLoading} trend={null} trendLabel={null} />
                <ERPKPICard title="Total Productos" value={productRanking?.length} subtitle="Productos analizados" icon="Package" iconColor="#C9A24D" valueColor="#C9A24D" loading={isLoading} trend={null} trendLabel={null} />
              </div>
            )}
            <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/5">
                <h3 className="text-sm font-bold font-oranienbaum text-[#D6EF4E]">Rentabilidad por Producto</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-oranienbaum">
                  <thead>
                    <tr className="border-b border-white/8 bg-[#141414]">
                      {['#', 'Producto', 'Facturación', 'Coste', 'Beneficio', 'Margen', 'Nº Ventas']?.map((h, i) => (
                        <th key={i} className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {isLoading ? (
                      <tr><td colSpan={7} className="py-10"><ERPLoading /></td></tr>
                    ) : !productRanking?.length ? (
                      <tr><td colSpan={7} className="py-10"><ERPEmpty message="Sin datos de productos" /></td></tr>
                    ) : productRanking?.map((p, idx) => (
                      <tr key={idx} className="hover:bg-white/3 transition-colors">
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${idx === 0 ? 'bg-[#D6EF4E]/20 text-[#D6EF4E]' : idx === 1 ? 'bg-white/10 text-white/60' : idx === 2 ? 'bg-[#C9A24D]/15 text-[#C9A24D]' : 'bg-white/5 text-white/40'}`}>{idx + 1}</span>
                        </td>
                        <td className="px-3 py-2.5"><span className="font-semibold text-white/90">{p?.producto}</span></td>
                        <td className="px-3 py-2.5"><span className="text-[#3A7F6B] font-semibold">{formatCurrency(p?.facturacion)}</span></td>
                        <td className="px-3 py-2.5"><span className="text-[#E05252]">{formatCurrency(p?.coste)}</span></td>
                        <td className="px-3 py-2.5"><span className={`font-semibold ${p?.beneficio >= 0 ? 'text-[#3A7F6B]' : 'text-[#E05252]'}`}>{formatCurrency(p?.beneficio)}</span></td>
                        <td className="px-3 py-2.5"><ERPBadge color={p?.margen >= 30 ? 'green' : p?.margen >= 15 ? 'gold' : p?.margen >= 0 ? 'default' : 'red'}>{formatPct(p?.margen)}</ERPBadge></td>
                        <td className="px-3 py-2.5"><span className="text-[#C9A24D] font-semibold">{p?.ventas}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* EMPLEADOS SECTION */}
        {activeSection === 'empleados' && (
          <div className="space-y-5 mb-5">
            {employeeRanking?.length > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <ERPKPICard title="Empleado Más Productivo" value={employeeRanking?.[0]?.empleado || '—'} subtitle={formatCurrency(employeeRanking?.[0]?.beneficio)} icon="UserCheck" iconColor="#3A7F6B" valueColor="#3A7F6B" loading={isLoading} trend={null} trendLabel={null} />
                <ERPKPICard title="Total Empleados" value={employeeRanking?.length} subtitle="Empleados analizados" icon="Users" iconColor="#8097D6" valueColor="#8097D6" loading={isLoading} trend={null} trendLabel={null} />
                <ERPKPICard title="Beneficio Medio/Empleado" value={formatCurrency(employeeRanking?.reduce((s, e) => s + e?.beneficio, 0) / Math.max(employeeRanking?.length, 1))} subtitle="Rentabilidad media" icon="BarChart3" iconColor="#C9A24D" valueColor="#C9A24D" loading={isLoading} trend={null} trendLabel={null} />
              </div>
            )}
            <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/5">
                <h3 className="text-sm font-bold font-oranienbaum text-[#D6EF4E]">Rentabilidad por Empleado</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-oranienbaum">
                  <thead>
                    <tr className="border-b border-white/8 bg-[#141414]">
                      {['#', 'Empleado', 'Facturación', 'Beneficio', 'Nº Trabajos', 'Kg Procesados', 'Beneficio/Trabajo', 'Beneficio/Kg']?.map((h, i) => (
                        <th key={i} className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {isLoading ? (
                      <tr><td colSpan={8} className="py-10"><ERPLoading /></td></tr>
                    ) : !employeeRanking?.length ? (
                      <tr><td colSpan={8} className="py-10"><ERPEmpty message="Sin datos de empleados" /></td></tr>
                    ) : employeeRanking?.map((e, idx) => (
                      <tr key={idx} className="hover:bg-white/3 transition-colors">
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${idx === 0 ? 'bg-[#D6EF4E]/20 text-[#D6EF4E]' : idx === 1 ? 'bg-white/10 text-white/60' : idx === 2 ? 'bg-[#C9A24D]/15 text-[#C9A24D]' : 'bg-white/5 text-white/40'}`}>{idx + 1}</span>
                        </td>
                        <td className="px-3 py-2.5"><span className="font-semibold text-white/90">{e?.empleado}</span></td>
                        <td className="px-3 py-2.5"><span className="text-[#3A7F6B] font-semibold">{formatCurrency(e?.facturacion)}</span></td>
                        <td className="px-3 py-2.5"><span className={`font-semibold ${e?.beneficio >= 0 ? 'text-[#3A7F6B]' : 'text-[#E05252]'}`}>{formatCurrency(e?.beneficio)}</span></td>
                        <td className="px-3 py-2.5"><span className="text-[#C9A24D] font-semibold">{e?.trabajos}</span></td>
                        <td className="px-3 py-2.5"><span className="text-white/60">{e?.kg?.toFixed(1)} kg</span></td>
                        <td className="px-3 py-2.5"><span className="text-[#8097D6]">{formatCurrency(e?.beneficioPorTrabajo)}</span></td>
                        <td className="px-3 py-2.5"><span className="text-[#D6EF4E]">{formatCurrency(e?.beneficioPorKg)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* DETALLE SECTION */}
        {activeSection === 'detalle' && (
          <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden mb-5">
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-base font-bold font-oranienbaum text-[#D6EF4E]">Detalle por Cliente</h2>
              <span className="text-xs font-oranienbaum text-white/40">{totalRecords} registros</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-oranienbaum">
                <thead>
                  <tr className="border-b border-white/8 bg-[#141414]">
                    {['#', 'Cliente', 'Categoría', 'Ingresos', 'Costes', 'Beneficio', 'Margen %', 'Loncheados']?.map((h, i) => (
                      <th key={i} className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {isLoading ? (
                    <tr><td colSpan={8} className="py-16"><ERPLoading /></td></tr>
                  ) : !paginatedTableData?.length ? (
                    <tr><td colSpan={8} className="py-16"><ERPEmpty message="No hay datos disponibles" /></td></tr>
                  ) : paginatedTableData?.map((row, idx) => {
                    const globalIdx = (currentPage - 1) * pageSize + idx + 1;
                    return (
                      <tr key={`${row?.clienteNombre}-${idx}`} className="hover:bg-white/3 transition-colors">
                        <td className="px-3 py-2.5"><span className="text-white/40 text-xs">{globalIdx}</span></td>
                        <td className="px-3 py-2.5"><span className="font-semibold text-white/90">{row?.clienteNombre || '—'}</span></td>
                        <td className="px-3 py-2.5">
                          <ERPBadge color={row?.categoria === 'minorista' ? 'blue' : row?.categoria === 'tienda' ? 'gold' : row?.categoria === 'bar' ? 'green' : row?.categoria === 'restaurante' ? 'red' : 'default'}>
                            {row?.categoria || '—'}
                          </ERPBadge>
                        </td>
                        <td className="px-3 py-2.5"><span className="font-semibold text-[#3A7F6B]">{formatCurrency(row?.ingresos)}</span></td>
                        <td className="px-3 py-2.5"><span className="text-[#E05252]">{formatCurrency(row?.costes)}</span></td>
                        <td className="px-3 py-2.5">
                          <span className={`font-semibold ${(row?.beneficio || 0) >= 0 ? 'text-[#3A7F6B]' : 'text-[#E05252]'}`}>
                            {formatCurrency(row?.beneficio)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <ERPBadge color={getMargenColor(row?.margen)}>{(row?.margen || 0)?.toFixed(1)}%</ERPBadge>
                        </td>
                        <td className="px-3 py-2.5"><span className="text-[#C9A24D] font-semibold">{row?.numeroLoncheados || 0}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalRecords > 0 && (
              <ERPPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
              />
            )}
          </div>
        )}
      </ERPPage>
    </>
  );
};

export default FinancialAnalysisDashboard;